/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Grid,
  InputAdornment,
  Paper,
  Snackbar,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import React, { ChangeEvent, useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  GetAllModelTypeArray,
  PartFamily,
  TransferOrderPartDetails,
} from "../../../@types/DRO/GetPartsListResponse";
import { ProductPartsBody } from "../../../@types/DTO/AddPartsBody";
import { AllPartApi } from "../../../api/GetParts/GetAllPartsApi";
import { useLoading } from "../../../views/Loader/LoadingContext";
import {
  AppAutoCompleteWithSearchAPI,
  OptionType,
} from "../../Layouts/AppAutoCompleteWithSearchAPI/AppAutoCompleteWithSearchAPI";

interface CreateProductTemplateOrderProps {
  onBack: () => void;
}

const CreateProductTemplateOrder: React.FC<CreateProductTemplateOrderProps> = ({
  onBack,
}) => {
  const [newItem, setNewItem] = useState({
    productName: "",
    salesPrice: "",
    taxes: "",
    oemType: null as number | null,
    vendorType: "",
    productType: "",
    cost: "",
    businessType: "",
    elementType: null as number | null,
    hsn: "",
    procurable: true,
    autoPriceUpdate: true,
    isSpecialPart: false,
    penaltyWaived: false,
    focCheck: true,
    proofType: "",
    partFamilyId: null as number | null,
  });
  const [isSalesPriceManual, setIsSalesPriceManual] = useState(false);
  const [selectedPart, setSelectedPart] =
    useState<TransferOrderPartDetails | null>(null);
  const [partTypes, setPartTypes] = useState<GetAllModelTypeArray[]>([]);
  const [partFamilies, setPartFamilies] = useState<PartFamily[]>([]);
  const [partFamiliesLoading, setPartFamiliesLoading] = useState(true);
  const [partFamiliesError, setPartFamiliesError] = useState<string | null>(
    null
  );
  const [selectedPartFamily, setSelectedPartFamily] =
    useState<PartFamily | null>(null);
  const [modelTypes, setModelTypes] = useState<GetAllModelTypeArray[]>([]);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [parts, setParts] = useState<TransferOrderPartDetails[]>([]);

  const [modelTypeMap, setModelTypeMap] = useState<
    Record<number, GetAllModelTypeArray>
  >({});
  const [partTypeMap, setPartTypeMap] = useState<
    Record<number, GetAllModelTypeArray>
  >({});
  const [selectedOEMTypeId, setSelectedOEMTypeId] = useState<number | null>(
    null
  );
  const [selectedPartIds, setSelectedPartIds] = useState<Set<number>>(
    new Set()
  );
  const [selectedPartValue, setSelectedPartValue] =
    useState<OptionType<TransferOrderPartDetails> | null>(null);
  const { setLoading } = useLoading();
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });
  const navigate = useNavigate();
  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: string
  ) => {
    const value = e.target.value;
    if (
      field === "quantity" ||
      field === "salesPrice" ||
      field === "cost" ||
      field === "taxes"
    ) {
      if (!/^\d*\.?\d*$/.test(value)) {
        setSnackbar({
          open: true,
          message: "Only numbers allowed",
          severity: "error",
        });
        return;
      }
    }
    if (field === "salesPrice") {
      setIsSalesPriceManual(true);
      setNewItem({ ...newItem, salesPrice: value });
      return;
    }

    if (field === "cost") {
      if (!isSalesPriceManual) {
        const numericCost = parseFloat(value);
        const calculatedSales =
          value === "" || Number.isNaN(numericCost)
            ? ""
            : (numericCost * 1.4).toFixed(2);
        setNewItem({
          ...newItem,
          cost: value,
          salesPrice: calculatedSales,
        });
      } else {
        setNewItem({ ...newItem, cost: value });
      }
      return;
    }

    setNewItem({ ...newItem, [field]: value });
  };

  const handleAutocompleteChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: GetAllModelTypeArray | null,
    field: string
  ) => {
    if (field === "oemType") {
      const newOEMTypeId = value ? value.id : null;
      setSelectedOEMTypeId(newOEMTypeId);
      setNewItem({ ...newItem, [field]: newOEMTypeId });
    } else if (field === "elementType") {
      const newElementTypeId = value ? value.id : null;
      const isConsumable = value?.description === "Consumable";
      const wasConsumable =
        !!newItem.elementType &&
        partTypeMap[newItem.elementType]?.description === "Consumable";
      setNewItem({
        ...newItem,
        [field]: newElementTypeId,
        penaltyWaived: isConsumable
          ? wasConsumable
            ? newItem.penaltyWaived
            : true
          : false,
      });
    } else {
      setNewItem({ ...newItem, [field]: value ? value.id : null });
    }
  };

  const fetchParts = useCallback(
    async (resetPage = false) => {
      try {
        const response = await AllPartApi.api.getPartList(
          resetPage ? 0 : page,
          rowsPerPage,
          searchQuery
        );
        setParts(response.data.data);
      } catch (error) {
        console.error("Error fetching parts", error);
      }
    },
    [page, rowsPerPage, searchQuery]
  );

  useEffect(() => {
    fetchParts();
  }, [fetchParts]);

  const handleScroll = (event: React.SyntheticEvent) => {
    const bottom =
      event.currentTarget.scrollHeight - event.currentTarget.scrollTop ===
      event.currentTarget.clientHeight;
    if (bottom) {
      setPage((prevPage) => prevPage + 1);
    }
  };
  const fetchPartTypes = useCallback(async () => {
    try {
      const response = await AllPartApi.api.getPartType();
      setPartTypes(response.data);
      const map = response.data.reduce((acc, type) => {
        acc[type.id] = type;
        return acc;
      }, {} as Record<number, GetAllModelTypeArray>);
      setPartTypeMap(map);
    } catch (error) {
      console.error("Error fetching part types", error);
    }
  }, []);

  const fetchModelTypes = useCallback(async () => {
    try {
      const response = await AllPartApi.api.getModelTypeAll();
      setModelTypes(response.data);
      const map = response.data.reduce((acc, type) => {
        acc[type.id] = type;
        return acc;
      }, {} as Record<number, GetAllModelTypeArray>);
      setModelTypeMap(map);
    } catch (error) {
      console.error("Error fetching model types", error);
    }
  }, []);

  useEffect(() => {
    fetchPartTypes();
    fetchModelTypes();
  }, [fetchPartTypes, fetchModelTypes]);

  useEffect(() => {
    const fetchPartFamilies = async () => {
      setPartFamiliesLoading(true);
      setPartFamiliesError(null);
      try {
        setPartFamilies(await AllPartApi.api.getPartFamilies(0, 10));
      } catch (error) {
        console.error("Error loading part families", error);
        setPartFamiliesError("Failed to load part families");
      } finally {
        setPartFamiliesLoading(false);
      }
    };

    fetchPartFamilies();
  }, []);

  const fetchPaginatedPartsBySearchTerm = useCallback(
    (
      currentPageNumber: number,
      currentSearchQueryTerm: string
    ): Promise<{
      options: OptionType<TransferOrderPartDetails>[];
      hasMore: boolean;
    }> => {
      const oemTypeIdToUse = selectedOEMTypeId;

      const fetchMultiplier = Math.max(
        2,
        Math.ceil(selectedPartIds.size / rowsPerPage) + 1
      );
      const adjustedPageSize = rowsPerPage * fetchMultiplier;

      return AllPartApi.api
        .getPartList(
          currentPageNumber,
          adjustedPageSize,
          currentSearchQueryTerm,
          undefined,
          undefined,
          oemTypeIdToUse || undefined
        )
        .then(({ data: response }) => {
          const availableParts = response.data.filter(
            (part) => !selectedPartIds.has(part.id)
          );

          return {
            options: availableParts?.map((part) => ({
              value: part.id.toString(),
              label: part.description,
              data: part,
            })),
            hasMore:
              currentPageNumber < response.totalPages - 1 ||
              availableParts.length >= rowsPerPage,
          };
        })
        .catch(() => ({ options: [], hasMore: false }));
    },
    [selectedOEMTypeId, selectedPartIds, rowsPerPage]
  );

  const handlePartSelect = (
    event: any,
    value: OptionType<TransferOrderPartDetails> | null
  ) => {
    // Only track the selected option; do not prefill other fields
    const oldId = selectedPartValue?.data?.id;
    const newId = value?.data?.id;

    if (oldId && oldId > 0) {
      setSelectedPartIds((prev) => {
        const next = new Set(prev);
        next.delete(oldId);
        return next;
      });
    }

    if (newId && newId > 0) {
      setSelectedPartIds((prev) => new Set(prev).add(newId));
    }

    setSelectedPartValue(value);
  };

  const handleCreateParts = async () => {
    setLoading(true);
    try {
      const isConsumable =
        newItem.elementType &&
        partTypeMap[newItem.elementType]?.description === "Consumable";

      const productPartsBody: ProductPartsBody = {
        description: newItem.productName,
        status: 1,
        tax: parseFloat(newItem.taxes) || 0,
        costPrice: parseFloat(newItem.cost) || 0,
        price: parseFloat(newItem.salesPrice) || 0,
        productType: newItem.productType,
        partType: newItem.elementType!.toString(),
        oemType: newItem.oemType!.toString(),
        vendorType: newItem.vendorType,
        businessType: newItem.businessType,
        hsn: newItem.hsn,
        procurable: newItem.procurable,
        autoPriceUpdate: newItem.autoPriceUpdate,
        isSpecialPart: newItem.isSpecialPart,
        ...(isConsumable && { penaltyWaived: newItem.penaltyWaived }),
        ...(newItem.partFamilyId !== null && {
          partFamilyId: newItem.partFamilyId,
        }),
        rootPartId: selectedPartValue?.data?.id,
        focCheck: newItem.focCheck,
        proofType: newItem.proofType,
      };
      await AllPartApi.api.addParts(productPartsBody);
      setSnackbar({
        open: true,
        message: "Parts created successfully",
        severity: "success",
      });
      setNewItem({
        productName: "",
        salesPrice: "",
        taxes: "",
        oemType: null,
        vendorType: "",
        productType: "",
        cost: "",
        businessType: "",
        elementType: null,
        hsn: "",
        procurable: true,
        autoPriceUpdate: false,
        isSpecialPart: false,
        penaltyWaived: false,
        focCheck: false,
        proofType: "",
        partFamilyId: null,
      });
      setSelectedPartFamily(null);
      setSelectedPartValue(null);
      setSelectedPartIds(new Set());
      setSelectedOEMTypeId(null);
      setTimeout(() => {
        navigate("/product-template");
      }, 2000);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Error creating parts",
        severity: "error",
      });
    }
    setLoading(false);
  };
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box sx={{ padding: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            fontWeight: "600",
            color: "#1E1E1E",
            textAlign: "left",
            fontSize: "1.5rem",
            mb: 1,
          }}>
          Create Parts
        </Typography>
        <div className="flex items-center bg-gray-100 px-4 py-2 rounded-md space-x-2">
          <Link
            to="/home"
            className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors">
            Dashboard
          </Link>
          <span className="text-sm text-gray-400">/</span>
          <Link
            to="/product-template"
            className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors">
            Parts List
          </Link>
          <span className="text-sm text-gray-400">/</span>
          <span className="text-sm font-medium text-gray-700">
            Create Parts
          </span>
        </div>
      </Box>

      {/* Form */}
      <Paper
        sx={{
          padding: 3,
          marginTop: 2,
          borderRadius: "8px",
          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
        }}>
        <Grid container spacing={3} sx={{ textAlign: "left" }}>
          <Grid item xs={12} sm={4}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ fontWeight: "500", mb: 1 }}>
              Product Name
            </Typography>
            <TextField
              fullWidth
              value={newItem.productName}
              onChange={(e) => handleInputChange(e, "productName")}
              variant="standard"
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ fontWeight: "500", mb: 1 }}>
              Part Family
            </Typography>
            <Autocomplete
              options={partFamilies}
              value={selectedPartFamily}
              loading={partFamiliesLoading}
              getOptionLabel={(option) => option.partFamily}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_, value) => {
                setSelectedPartFamily(value);
                setNewItem({ ...newItem, partFamilyId: value?.id ?? null });
              }}
              noOptionsText={
                partFamiliesError ?? "No part families available"
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="standard"
                  error={!!partFamiliesError}
                  helperText={partFamiliesError}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ fontWeight: "500", mb: 1 }}>
              Part Location
            </Typography>
            <TextField
              fullWidth
              disabled
              value={selectedPartFamily?.partLocation ?? ""}
              variant="standard"
              InputProps={{ readOnly: true }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ fontWeight: "500", mb: 1 }}>
              HSN
            </Typography>
            <TextField
              fullWidth
              value={newItem.hsn}
              onChange={(e) => handleInputChange(e, "hsn")}
              variant="standard"
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ fontWeight: "500", mb: 1 }}>
              Cost
            </Typography>
            <TextField
              fullWidth
              value={newItem.cost}
              onChange={(e) => handleInputChange(e, "cost")}
              variant="standard"
              inputProps={{
                maxLength: 10,
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">₹</InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ fontWeight: "500", mb: 1 }}>
              Taxes
            </Typography>
            <TextField
              fullWidth
              value={newItem.taxes}
              onChange={(e) => handleInputChange(e, "taxes")}
              variant="standard"
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ fontWeight: "500", mb: 1 }}>
              OEM Type
            </Typography>
            <Autocomplete
              options={modelTypes}
              getOptionLabel={(option) => option.description}
              value={modelTypeMap[newItem.oemType!] || null}
              onChange={(event, value) =>
                handleAutocompleteChange(event, value, "oemType")
              }
              renderInput={(params) => (
                <TextField {...params} variant="standard" />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ fontWeight: "500", mb: 1 }}>
              Vendor Type
            </Typography>
            <Autocomplete
              options={["LV", "EM", "SE"]}
              value={newItem.vendorType}
              onChange={(event, value) =>
                setNewItem({ ...newItem, vendorType: value || "" })
              }
              renderInput={(params) => (
                <TextField {...params} variant="standard" />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ fontWeight: "500", mb: 1 }}>
              Product Type
            </Typography>
            <Autocomplete
              options={["PRODUCT", "SERVICE"]}
              value={newItem.productType}
              onChange={(event, value) =>
                setNewItem({ ...newItem, productType: value || "" })
              }
              renderInput={(params) => (
                <TextField {...params} variant="standard" />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ fontWeight: "500", mb: 1 }}>
              Sales Price
            </Typography>
            <TextField
              fullWidth
              value={newItem.salesPrice}
              onChange={(e) => handleInputChange(e, "salesPrice")}
              variant="standard"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">₹</InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ fontWeight: "500", mb: 1 }}>
              Business Type
            </Typography>
            <Autocomplete
              options={["TWO_WHEELER", "THREE_WHEELER"]}
              value={newItem.businessType}
              onChange={(event, value) =>
                setNewItem({ ...newItem, businessType: value || "" })
              }
              renderInput={(params) => (
                <TextField {...params} variant="standard" />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ fontWeight: "500", mb: 1 }}>
              Element Type
            </Typography>
            <Autocomplete
              options={partTypes}
              getOptionLabel={(option) => option.description}
              value={partTypeMap[newItem.elementType!] || null}
              onChange={(event, value) =>
                handleAutocompleteChange(event, value, "elementType")
              }
              renderInput={(params) => (
                <TextField {...params} variant="standard" />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Box sx={{ display: "flex", gap: 4 }}>
              <Box>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  sx={{ fontWeight: "500", mb: 1 }}>
                  Procurable
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Switch
                    checked={newItem.procurable}
                    onChange={(e) =>
                      setNewItem({ ...newItem, procurable: e.target.checked })
                    }
                    color="primary"
                  />
                  <Typography variant="body2" sx={{ ml: 1 }}>
                    {newItem.procurable ? "True" : "False"}
                  </Typography>
                </Box>
              </Box>

              <Box>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  sx={{ fontWeight: "500", mb: 1 }}>
                  Special Part
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Switch
                    checked={newItem.isSpecialPart}
                    onChange={(e) =>
                      setNewItem({
                        ...newItem,
                        isSpecialPart: e.target.checked,
                      })
                    }
                    color="primary"
                  />
                  <Typography variant="body2" sx={{ ml: 1 }}>
                    {newItem.isSpecialPart ? "True" : "False"}
                  </Typography>
                </Box>
              </Box>

              {newItem.elementType &&
                partTypeMap[newItem.elementType]?.description === "Consumable" && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      color="textSecondary"
                      sx={{ fontWeight: "500", mb: 1 }}>
                      Penalty Waived
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Switch
                        checked={newItem.penaltyWaived}
                        onChange={(e) =>
                          setNewItem({ ...newItem, penaltyWaived: e.target.checked })
                        }
                        color="primary"
                      />
                      <Typography variant="body2" sx={{ ml: 1 }}>
                        {newItem.penaltyWaived ? "True" : "False"}
                      </Typography>
                    </Box>
                  </Box>
                )}
            </Box>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ fontWeight: "500", mb: 1 }}>
              Auto Price Update
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Switch
                checked={newItem.autoPriceUpdate}
                onChange={(e) =>
                  setNewItem({ ...newItem, autoPriceUpdate: e.target.checked })
                }
                color="primary"
              />
              <Typography variant="body2" sx={{ ml: 1 }}>
                {newItem.autoPriceUpdate ? "True" : "False"}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ fontWeight: "500", mb: 1 }}>
              FOC Check
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Switch
                checked={newItem.focCheck}
                onChange={(e) =>
                  setNewItem({ ...newItem, focCheck: e.target.checked })
                }
                color="primary"
              />
              <Typography variant="body2" sx={{ ml: 1 }}>
                {newItem.focCheck ? "True" : "False"}
              </Typography>
            </Box>
          </Grid>

          {newItem.focCheck && (
            <Grid item xs={12} sm={4}>
              <Typography
                variant="subtitle2"
                color="textSecondary"
                sx={{ fontWeight: "500", mb: 1 }}>
                Proof Type
              </Typography>
              <Autocomplete
                options={["Photo", "Video"]}
                value={newItem.proofType}
                onChange={(event, value) =>
                  setNewItem({ ...newItem, proofType: value || "" })
                }
                renderInput={(params) => (
                  <TextField {...params} variant="standard" />
                )}
              />
            </Grid>
          )}

          {/* Root Part (Product Description selector) - moved to last */}
          <Grid item xs={12} sm={4}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ fontWeight: "500", mb: 1 }}>
              Root Part Id (Optional)
            </Typography>
            <AppAutoCompleteWithSearchAPI
              key={`product-select-oem-${selectedOEMTypeId || "none"}`}
              onChange={handlePartSelect}
              initialPageNumber={0}
              value={selectedPartValue}
              getData={function (data: {
                currentPageNumber: number;
                currentSearchQueryTerm: string;
              }): Promise<{
                options: OptionType<TransferOrderPartDetails>[];
                hasMore: boolean;
              }> {
                return fetchPaginatedPartsBySearchTerm(
                  data.currentPageNumber,
                  data.currentSearchQueryTerm
                );
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Actions */}
      <Box sx={{ marginTop: 4, textAlign: "right" }}>
        <Box>
          <Button
            variant="contained"
            onClick={handleCreateParts}
            color="primary"
            sx={{
              padding: "10px 20px",
              borderRadius: "32px",
              backgroundColor: "#1E1E1E",
              textTransform: "none",
              fontWeight: "500",
              "&:hover": { backgroundColor: "#000000" },
            }}>
            Create Parts
          </Button>
        </Box>
      </Box>
      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CreateProductTemplateOrder;
